import React, { useState, useRef, useEffect } from 'react';
import { PageProgressBar } from "../themes/userq/ProgressBar/page_progress_bar";
import { Text } from "../themes/userq/Text/Text";
import { TextArea } from "../themes/userq/TextArea/TextArea";
import { Button } from "../themes/userq/Button/Button";
import { Icon } from "../themes/userq/Icon/Icon";
import { TesterQuestionOptions } from "../themes/userq/TesterQuestionOptions/TesterQuestionOptions";
import { Input } from "../themes/userq/Input/Input";
import { RadioButton } from "../themes/userq/RadioButton/RadioButton";
import { RatingScaleTester } from "../themes/userq/RatingScaleTester/RatingScaleTester";
import { TakeTestQuestionImage } from '../themes/userq/QuestionImage/TakeTestQuestionImage';
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { isEmpty } from "lodash";
import { showToast } from "../../lib/helpers";

const GlobalQuestion = ({
  test,
  question,
  currentStep,
  totalSteps,
  onNext,
  onSkip,
  isOptional: isOptionalProp,
  timeSpent,
  questionType,
  result,
  questionCallback,
  callback,
  methodology = 'question', // Default to 'question' methodology
  onBackToExplore, // New prop for prototype test
  isPrototypeTest = false, // New prop to identify prototype test
  prototypeTask = null, // New prop for prototype task data
  skippedCount = 0, // Add skippedCount as prop
  onSkipCountChange, // Add callback for skip count changes
}) => {
  // Common state
  const [formValues, setFormValues] = useState({
    type: null,
    question_id: null,
    answer: '',
    option_id: [],
    is_optional: 0
  });
  const [formErrors, setFormErrors] = useState({ error: '', error_class: '' });
  const [questionLoading, setQuestionLoading] = useState(false);
  const [questionTimeSpend, setQuestionTimeSpend] = useState(0);
  const [likertActive, setLikertActive] = useState(false);
  const [options, setOptions] = useState([]);
  const [placeholderProps, setPlaceholderProps] = useState({});
  const [isNoneOfTheAboveSelected, setIsNoneOfTheAboveSelected] = useState(false);
  const [rankingOptions, setRankingOptions] = useState([]);
  const [otherOptionSelected, setOtherOptionSelected] = useState(false);

  // Refs
  const isTyping = useRef(false);
  const isActiveQuestionId = useRef(null);
  const isSkipped = useRef(0);
  const isOptional = useRef(isOptionalProp);
  const nextButtonActive = useRef(false);
  const initialRankingOrder = useRef([]);
  const rankingInteracted = useRef(false);
  const likertAttemptedSubQuestion = useRef(0);
  const queryAttr = "data-rbd-drag-handle-draggable-id";

  // Initialize question state
  useEffect(() => {
    if (question) {
      const questionData = question.questionData;  // This contains the parsed questions data
      const questionInfo = question.data;  // This contains the raw question data
      
      // Update isOptional ref when question changes
      isOptional.current = questionData.is_optional;
      // Reset ranking baseline unless set below for ranking questions
      initialRankingOrder.current = [];
      rankingInteracted.current = false;
      
      // Set initial form values based on question type and methodology
      const getQuestionType = (type) => {
        switch(type) {
          case 'singlechoice':
            return 'single';
          case 'multiplechoice':
            return 'multiple';
          case 'likertscale':
            return 'likertscale';
          case 'likertscale_single':
            return 'likertscale_single';
          case 'ratingscale':
            return 'ratingscale';
          case 'rankingscale':
            return 'rankingscale';
          default:
            return 'free';
        }
      };

      const questionType = getQuestionType(questionInfo.question_type);
      //console.log('Setting question type:', questionType, 'for question type:', questionInfo.question_type);
      
      if (questionInfo.question_type === 'likertscale' || questionInfo.question_type === 'likertscale_single') {
          const subquestions = getSubquestions(question);
          setFormValues({ 
          type: questionType,
          question_id: questionInfo.id,
            is_optional: questionData.is_optional,
            answer: '',
            option_id: [],
            totalSubQuestions: subquestions?.length || 0
          });
          setLikertActive(true);
      } else if (questionInfo.question_type === 'rankingscale') {
          const options = getOptions(question);
          let ranking_options = [...(options || [])];
          if (questionData.randomize_ranking_options === 1) {
            ranking_options = shuffle(ranking_options);
          }
          setRankingOptions(ranking_options);
          initialRankingOrder.current = ranking_options.map(option => option.id);
          setFormValues({
          type: questionType,
          question_id: questionInfo.id,
            is_optional: questionData.is_optional,
            answer: '',
            option_id: ranking_options.map(option => option.id)
          });
        } else {
          setFormValues({ 
          type: questionType,
          question_id: questionInfo.id,
            is_optional: questionData.is_optional,
            answer: '',
            option_id: []
          });
      }

      // Reset states
      isActiveQuestionId.current = questionInfo.id;
      isSkipped.current = 0;
      nextButtonActive.current = false;
      likertAttemptedSubQuestion.current = 0;
      setOtherOptionSelected(false);
      setIsNoneOfTheAboveSelected(false);
    }

    // Start timer
    const timer = setInterval(() => {
      setQuestionTimeSpend(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [question, methodology]);

  // Common validation functions
  const validateQuestion = () => {
    if (isSkipped.current === 0) {
      // Check if question is optional and empty
      if (formValues.is_optional === 1 && 
          ((formValues.answer === '' && !['multiple', 'rankingscale', 'likertscale', 'likertscale_single'].includes(formValues.type)) || 
           (['multiple', 'rankingscale'].includes(formValues.type) && formValues.option_id.length === 0) ||
           (['likertscale', 'likertscale_single'].includes(formValues.type) && formValues.option_id.length === 0))) {
        
        if (skippedCount < 2) {
          // Don't allow skipping for non-optional likert scale questions
          if (formValues.is_optional === 0 && ['likertscale', 'likertscale_single'].includes(formValues.type)) {
            isSkipped.current = 0;
          } else {
            isSkipped.current = 1;
            setFormValues({
              ...formValues,
              question_id: isActiveQuestionId.current,
            });
            return true; // Allow skipping
          }
        } else {
          showToast("You can skip maximum of 2 questions.", "error");
          return false;
      }
    }

    if (isSkipped.current === 0) {
      switch (formValues.type) {
        case 'single':
        case 'ratingscale':
          if (formValues.answer === '') {
            setFormErrors({ error: 'Required field', error_class: 'input_error' });
            showToast("You need to select one answer before moving to the next question.", "error");
            return false;
          }
            // Check for "Other" option validation in single choice
            const options = getOptions(question);
            const selectedOption = options?.find(opt => opt.id === formValues.answer);
            if (selectedOption && 
                ((test.language === "en" && selectedOption.option_value === "Other") ||
                 (test.language === "ar" && selectedOption.option_value === "آخر"))) {
              if (!formValues.other_option_value || formValues.other_option_value.trim() === '') {
                setFormErrors({ error: 'Required field', error_class: 'input_error' });
                return false;
              }
            }
          break;

        case 'multiple':
          return validateMultipleChoice();

        case 'rankingscale':
            if (formValues.option_id.length === 0) {
            setFormErrors({ error: 'Required field', error_class: 'input_error' });
            showToast("You need to select one answer before moving to the next question.", "error");
            return false;
          }
            // Check if options have been reordered. If the user has interacted with the
            // ranking list (dragged at least once) we allow submission even if they have
            // reverted to the original order.
            const originalOptions = initialRankingOrder.current.length
              ? initialRankingOrder.current
              : getOptions(question).map(option => option.id);
            const currentOptions = formValues.option_id;
            const hasBeenReordered = JSON.stringify(originalOptions) !== JSON.stringify(currentOptions);
            if (!hasBeenReordered && !rankingInteracted.current) {
              showToast("You need to reorder the items before moving to the next question.", "error");
              return false;
            }
          break;

        case 'likertscale':
          case 'likertscale_single':
            if (formValues.option_id.length === 0) {
            setFormErrors({ error: 'Required field', error_class: 'input_error' });
              showToast(formValues.type === 'likertscale' ? 
                "You need to answer for each subquestion." : 
                "You need to answer before moving to the next question.", "error");
            return false;
          }
            // Check if all subquestions have been answered
            const answeredSubquestions = new Set(formValues.option_id.map(opt => opt.subquestionid));
            if (answeredSubquestions.size < formValues.totalSubQuestions) {
            setFormErrors({ error: 'Required field', error_class: 'input_error' });
              showToast(formValues.type === 'likertscale' ? 
                "You need to answer for each subquestion." : 
                "You need to answer before moving to the next question.", "error");
            return false;
          }
          break;

        case 'free':
          if (!formValues.answer || formValues.answer.trim() === '') {
            setFormErrors({ error: 'Required field', error_class: 'input_error' });
            showToast("You need to enter an answer before moving to the next question.", "error");
            return false;
          }
          break;
      }
      }
      return true;
    }
    return true;
  };

  const validateMultipleChoice = () => {
    const mc_question = question;
    let mc_error = false;
    let mc_error_message = "You need to select at least 1 option before moving to the next question.";
    let other_form_error = false;
    let other_selected = false;

    formValues.option_id.forEach(option_id => {
      const options = getOptions(question);
      if (options) {
        options.forEach(option => {
          if (option.id === option_id && 
              ((test.language === "en" && option.option_value === "Other") || 
               (option.option_value === "آخر" && test.language === "ar"))) {
            other_selected = true;
          }
        });
      }
    });

    if (isNoneOfTheAboveSelected) {
      mc_error = false;
    } else if (other_selected && !formValues.other_option_value) {
      other_form_error = true;
      mc_error = true;
    } else {
      const questionData = question.questionData;
      if (questionData) {
        if (questionData.exact) {
          if (formValues.option_id.length !== questionData.exact) {
            mc_error = true;
            mc_error_message = `You need to select ${questionData.exact} options.`;
          }
        } else if (questionData.min && questionData.max) {
          if (!(formValues.option_id.length >= questionData.min && formValues.option_id.length <= questionData.max)) {
            mc_error = true;
            mc_error_message = formValues.option_id.length < questionData.min
              ? `You need to select at least ${questionData.min} options before moving to the next question.`
              : `You can select up to ${questionData.max} options.`;
          }
        } else if (questionData.min) {
          if (formValues.option_id.length < questionData.min) {
            mc_error = true;
            mc_error_message = `You need to select at least ${questionData.min} options before moving to the next question.`;
          }
        } else if (questionData.max) {
          if (formValues.option_id.length > questionData.max) {
            mc_error = true;
            mc_error_message = `You can select up to ${questionData.max} options.`;
          }
        } else if (formValues.option_id.length === 0) {
          mc_error = true;
          mc_error_message = "You need to select one answer before moving to the next question.";
        }
      } else {
        if (formValues.option_id.length === 0) {
          mc_error = true;
          mc_error_message = "You need to select at least 1 option before moving to the next question.";
        }
      }
    }

    if (mc_error) {
      if (other_form_error) {
        setFormErrors({ error: 'Required field', error_class: 'input_error' });
      } else {
        showToast(mc_error_message, "error");
      }
      return false;
    }
    return true;
  };

  // Drag and drop handlers
  const handleDragStart = (event) => {
    const draggedDOM = getDraggedDom(event.draggableId);
    if (!draggedDOM) return;

    const { clientHeight, clientWidth } = draggedDOM;
    const sourceIndex = event.source.index;
    const clientY = calculateClientY(draggedDOM, sourceIndex);

    setPlaceholderProps({
      clientHeight,
      clientWidth,
      clientY,
      clientX: parseFloat(window.getComputedStyle(draggedDOM.parentNode).paddingLeft),
      background: '#CCCCCC',
      border: 'none'
    });
  };

  const handleDragUpdate = (event) => {
    if (!event.destination) return;

    const draggedDOM = getDraggedDom(event.draggableId);
    if (!draggedDOM) return;

    const { clientHeight, clientWidth } = draggedDOM;
    const destinationIndex = event.destination.index;
    const sourceIndex = event.source.index;

    const childrenArray = [...draggedDOM.parentNode.children];
    const movedItem = childrenArray[sourceIndex];
    childrenArray.splice(sourceIndex, 1);

    const updatedArray = [
      ...childrenArray.slice(0, destinationIndex),
      movedItem,
      ...childrenArray.slice(destinationIndex + 1),
    ];

    const clientY = calculateClientY(draggedDOM, destinationIndex, updatedArray);

    setPlaceholderProps({
      clientHeight,
      clientWidth,
      clientY,
      clientX: parseFloat(window.getComputedStyle(draggedDOM.parentNode).paddingLeft),
      background: 'none',
      border: '1px dashed'
    });
  };

  const calculateClientY = (draggedDOM, index, array = null) => {
    const children = array || [...draggedDOM.parentNode.children];
    return parseFloat(window.getComputedStyle(draggedDOM.parentNode).paddingTop) +
      children.slice(0, index).reduce((total, curr) => {
        const style = curr.currentStyle || window.getComputedStyle(curr);
        const marginBottom = parseFloat(style.marginBottom);
        return total + curr.clientHeight + marginBottom;
      }, 0);
  };

  const getDraggedDom = (draggableId) => {
    const domQuery = `[${queryAttr}='${draggableId}']`;
    return document.querySelector(domQuery);
  };

  // Next button state handlers
  const updateNextBtnStateThruOptions = (items, formValues, isNoneOfTheAbove) => {
    if (isNoneOfTheAbove) {
      nextButtonActive.current = true;
      return;
    }

    const questionData = items.questionData;
    if (!questionData) {
      nextButtonActive.current = formValues.option_id.length > 0;
      return;
    }

    // Check if "Other" option is selected
    const hasOtherOption = formValues.option_id.some(id => {
      const options = getOptions(items);
      const option = options?.find(opt => opt.id === id);
      return (test.language === "en" && option?.option_value === "Other") || 
             (test.language === "ar" && option?.option_value === "آخر");
    });

    if (hasOtherOption && !formValues.other_option_value) {
      nextButtonActive.current = false;
      return;
    }

    // Handle exact number of selections
    if (questionData.exact) {
      nextButtonActive.current = formValues.option_id.length === questionData.exact;
      return;
    }

    // Handle min-max range
    if (questionData.min && questionData.max) {
      nextButtonActive.current = formValues.option_id.length >= questionData.min && 
                                formValues.option_id.length <= questionData.max;
      return;
    }

    // Handle minimum selections
    if (questionData.min) {
      nextButtonActive.current = formValues.option_id.length >= questionData.min;
      return;
    }

    // Handle maximum selections
    if (questionData.max) {
      nextButtonActive.current = formValues.option_id.length <= questionData.max;
      return;
    }

    // Default case - at least one selection
    nextButtonActive.current = formValues.option_id.length > 0;
  };

  const updateNextBtnStateThruOther = (items, formValues) => {
    if (!formValues.other_option_value) {
      nextButtonActive.current = false;
      return;
    }

    const questionData = items.questionData;
    if (!questionData) {
      nextButtonActive.current = true;
      return;
    }

    // Handle exact number of selections
    if (questionData.exact) {
      nextButtonActive.current = formValues.option_id.length === questionData.exact;
      return;
    }

    // Handle min-max range
    if (questionData.min && questionData.max) {
      nextButtonActive.current = formValues.option_id.length >= questionData.min && 
                                formValues.option_id.length <= questionData.max;
      return;
    }

    // Handle minimum selections
    if (questionData.min) {
      nextButtonActive.current = formValues.option_id.length >= questionData.min;
      return;
    }

    // Handle maximum selections
    if (questionData.max) {
      nextButtonActive.current = formValues.option_id.length <= questionData.max;
      return;
    }

    // Default case - at least one selection
    nextButtonActive.current = formValues.option_id.length > 0;
  };

  // Question submission handler
  const handleSubmitQuestion = (e) => {
    e.preventDefault();
    setFormErrors({ error: '', error_class: '' });
    
    if (isSkipped.current === 0) {
    if (!nextButtonActive.current) {
      if (formValues.type === 'single' || formValues.type === 'ratingscale') {
        if (formValues.answer === '') {
          setFormErrors({ error: 'Required field', error_class: 'input_error' });
          showToast("You need to select one answer before moving to the next question.", "error");
          return;
        }
          // Validate "Other" option for single choice
          const options = getOptions(question);
          const selectedOption = options?.find(opt => opt.id === formValues.answer);
          if (selectedOption && 
              ((test.language === "en" && selectedOption.option_value === "Other") ||
              (test.language === "ar" && selectedOption.option_value === "آخر"))) {
            if (!formValues.other_option_value || formValues.other_option_value.trim() === '') {
              setFormErrors({ error: 'Required field', error_class: 'input_error' });
              return;
            }
          }
      } else if (formValues.type === 'multiple') {
        if (!validateMultipleChoice()) {
          return;
        }
        } else if (formValues.type === 'likertscale' || formValues.type === 'likertscale_single') {
        if (formValues.option_id.length === 0 || formValues.option_id.length < formValues.totalSubQuestions) {
            showToast(formValues.type === 'likertscale' ? 
              "You need to answer for each subquestion." : 
              "You need to answer before moving to the next question.", "error");
          return;
        }
        } else if (formValues.type === 'rankingscale') {
        const originalOptions = initialRankingOrder.current.length
          ? initialRankingOrder.current
          : getOptions(question).map(option => option.id);
        const currentOptions = formValues.option_id;
        const hasBeenReordered = JSON.stringify(originalOptions) !== JSON.stringify(currentOptions);
        // Allow submission if user has interacted with the list even if they ended up
        // with the original order again.
        if (!hasBeenReordered && !rankingInteracted.current) {
          showToast("You need to reorder the items before moving to the next question.", "error");
          return;
        }
      } else if (formValues.type === 'free') {
        if (!formValues.answer || formValues.answer.trim() === '') {
          setFormErrors({ error: 'Required field', error_class: 'input_error' });
          showToast("You need to enter an answer before moving to the next question.", "error");
          return;
        }
      }
      return;
    }

    if (!validateQuestion()) {
      return;
      }
    }

    // Handle methodology-specific logic
    switch (methodology) {
      case 'survey':
        // For multiple choice questions, validate the selection count
        if (question.data.question_type === 'multiplechoice') {
            if (!validateMultipleChoice()) {
                return;
            }
        }

        // Check question jump logic
        if (question.data.jump_to_question) {
            if (question.data.jump_to_question === 'End Survey') {
                onNext({
                    ...formValues,
                    question_time_spent: questionTimeSpend,
                    is_skipped: isSkipped.current
                }, true);
                return;
            } else {
                const jumpToQuestion = parseInt(question.data.jump_to_question);
                onNext({
                    ...formValues,
                    question_time_spent: questionTimeSpend,
                    is_skipped: isSkipped.current
                }, false, jumpToQuestion);
                return;
            }
        }

        // Check option-specific jump logic for single choice
        if (question.data.question_type === 'singlechoice') {
            const chosenOption = question.data.surveyoptions?.find(option => 
                option.id === parseInt(formValues.answer)
            );

            if (chosenOption?.jump_to_question) {
                if (chosenOption.jump_to_question === 'End Survey') {
                    onNext({
                        ...formValues,
                        question_time_spent: questionTimeSpend,
                        is_skipped: isSkipped.current
                    }, true);
                    return;
                } else {
                    const jumpToQuestion = parseInt(chosenOption.jump_to_question);
                    onNext({
                        ...formValues,
                        question_time_spent: questionTimeSpend,
                        is_skipped: isSkipped.current
                    }, false, jumpToQuestion);
                    return;
                }
            }
        }
        break;

      case 'prototype':
        // Handle prototype-specific jump logic
        if (formValues.type === 'single') {
          const chosenOption = getOptions(question)?.find(option => 
            option.id === formValues.answer
          );

          if (chosenOption?.jump_to_question) {
            if (chosenOption.jump_to_question === 'End Survey') {
                    onNext({
                        ...formValues,
                        question_time_spent: questionTimeSpend,
                        is_skipped: isSkipped.current
                    }, true);
                    return;
                } else {
              const jumpToQuestion = parseInt(chosenOption.jump_to_question);
                    onNext({
                        ...formValues,
                        question_time_spent: questionTimeSpend,
                        is_skipped: isSkipped.current
                    }, false, jumpToQuestion);
                    return;
                }
            }
        }

        if (question.questionData.is_logic === 1) {
          if (question.data.jump_to_question === 'End Survey') {
            onNext({
              ...formValues,
              question_time_spent: questionTimeSpend,
              is_skipped: isSkipped.current
            }, true);
            return;
          } else if (question.data.jump_to_question) {
            const jumpToQuestion = parseInt(question.data.jump_to_question);
            onNext({
              ...formValues,
              question_time_spent: questionTimeSpend,
              is_skipped: isSkipped.current
            }, false, jumpToQuestion);
            return;
          }
        }
        break;
    }

    // Normal flow without jump logic
    onNext({
      ...formValues,
      question_time_spent: questionTimeSpend,
      is_skipped: isSkipped.current
    });
  };

  const renderQuestionHeader = () => (
    <div>
      <div className="taking-test-progressbar">
        <PageProgressBar 
          language={test.language} 
          current_step={currentStep} 
          total_steps={totalSteps} 
          variant="take-test"
          label={test.language === "en" ? "Question" : "سؤال"} 
        />
      </div>

      <div className="take-test-heading-wrap">
        <Text type="h2">
          {question.questionData.question}
          {question.questionData.is_optional ? (
            <span className="optional-span">
              {test.language === "en" ? " (Optional)" : " (اختياري)"}
            </span>
          ) : null}
        </Text>
      </div>

      {question.questionData.image_url ? (
        <TakeTestQuestionImage 
          type="question"
          url={question.questionData.image_url} 
        />
      ) : null}
    </div>
  );

  const renderQuestionContent = () => {
    switch (question.data.question_type) {
      case 'singlechoice':
        return renderSingleChoice();
      case 'multiplechoice':
        return renderMultipleChoice();
      case 'ratingscale':
        return renderRatingScale();
      case 'likertscale':
      case 'likertscale_single':
        return renderLikertScale();
      case 'rankingscale':
        return renderRankingScale();
      default:
        return renderFreeText();
    }
  };

  // Add shuffle function
  const shuffle = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  // Add movetonextlikertsubquestion function
  const movetonextlikertsubquestion = (currentIndex) => {
    const subquestions = methodology === 'survey' ? question.data.surveysubquestions : question.data.testsubquestions;
    if (!subquestions) return;
    
    const nextIndex = currentIndex + 1;
    if (nextIndex < subquestions.length) {
      const nextElement = document.getElementById(`subquestion-${nextIndex}`);
      if (nextElement) {
        nextElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Add tree test methodology handling
  const getOptions = (question) => {
    if (!question || !question.data) return [];

    switch (methodology) {
      case 'survey':
        return question.data.surveyoptions || [];
      case 'five_seconds':
        return question.data.fivesecondstestoptions || [];
      case 'firstclick':
        return question.data.firstclicktestoptions || [];
      case 'preference':
        return question.data.preferencetestoptions || [];
      case 'tree':
        // For tree test, check both data and questionData for options
        return question.data.treetestoptions || question.questionData.options || [];
      case 'prototype':
        return question.data.prototypetestoptions || [];
      default:
        // For backward compatibility and default case
        return question.data.options || question.questionData.options || [];
    }
  };

  const getSubquestions = (question) => {
    if (!question || !question.data) return [];

    switch (methodology) {
      case 'survey':
        return question.data.surveysubquestions || [];
      case 'five_seconds':
        return question.data.fivesecondstestsubquestions || [];
      case 'firstclick':
        return question.data.firstclicktesttasksubquestions || [];
      case 'preference':
        return question.data.preferencetestsubquestions || [];
      case 'tree':
        return question.data.treetesttasksubquestions || [];
      case 'prototype':
        return question.data.prototypetesttasksubquestions || [];
      default:
        return question.data.testsubquestions || [];
    }
  };

  // Add methodology-specific class handling
  const getMethodologyClass = (baseClass) => {
    if (methodology === 'prototype') {
      return `${baseClass} prototype-methodology`;
    }
    return baseClass;
  };

  // Add prototype-specific button rendering
  const renderPrototypeButtons = () => (
    <div className={`taking-test-button-wrap proto-type-take-test-btn d-flex${isTyping.current ? ' keyboard-active-button' : ''}${isOptional.current ? ' optional-button' : ''}`} id="buttons">
     
        <Button 
          type="secondary" 
          size="large" 
          label={test.language === "en" ? "Back to explore" : "العودة إلى التصفح"} 
          iconLeft={<Icon colorClass="gray-900-svg" value={test.language === "ar" ? "forward-arrow" : "back-arrow"} />} 
          onClick={onBackToExplore}
        />
    
      
        <Button 
          type="primary" 
          size="large" 
          label={test.language === "en" ? "Next" : "التالي"} 
          iconRight={<Icon colorClass="gray-50-svg" value={test.language === "ar" ? "back-arrow" : "forward-arrow"} />} 
          state={nextButtonActive.current ? 'active' : 'disabled'} 
        />
     
      {isOptional.current ? (
        <div className="skipp-btn-option">
          <Button 
            type="ghost" 
            size="medium" 
            label={test.language === "en" ? "Skip" : "تخطي"} 
            iconRight={<Icon colorClass="gray-900-svg" value={test.language === "ar" ? "backward" : "forward"} />} 
            onClick={(e) => {
              e.preventDefault();
              if (skippedCount < 2) {
                isSkipped.current = 1;
                setFormValues({
                  ...formValues,
                  question_id: isActiveQuestionId.current,
                });
                const skipData = {
                  ...formValues,
                  question_time_spent: questionTimeSpend,
                  is_skipped: 1
                };
                onNext(skipData);
                onSkipCountChange(skippedCount + 1);
              } else {
                showToast("You can skip maximum of 2 questions.", "error");
              }
            }}
            cssclass="ml-16"
          />
        </div>
      ) : <div className="skipp-btn-option"></div>}
    </div>
  );

  const renderSingleChoice = () => {
    const options = getOptions(question);

    return (
      <div className={getMethodologyClass("single-choice-take-test medium-box-wrapper")}>
        {renderQuestionHeader()}
        
        <div className="select-option-text-guide mt-24">
          <Text type="body-text-1" fontWeight="medium-font">
            {test.language === "en" ? "Select 1 of the following" : "حدد خيار واحد"}
          </Text>
        </div>

        <div className="test-form-hold mt-32">
          <div className={`${test.language === "en" ? "singlechoice-option-wrap" : "singlechoice-option-wrap singlechoice-option-arabic-wrap"}`}>
            {options?.map((optionData) => (
              <div key={optionData.id}>
                <TesterQuestionOptions
                  id={`option-select-${optionData.id}`}
                  type="singlechoice"
                  value={String(optionData.id)}
                  label={optionData.option_value}
                  isChecked={formValues.answer === optionData.id}
                  onChange={(e) => {
                    isActiveQuestionId.current = question.data.id;

                    if ((test.language === "en" && optionData.option_value === "Other") || 
                        (optionData.option_value === "آخر" && test.language === "ar")) {
                      setFormValues({
                        ...formValues,
                        question_id: question.data.id,
                        answer: parseInt(e.target.value),
                        other_option_value: "",
                      });
                      setOtherOptionSelected(true);
                      nextButtonActive.current = false;
                    } else {
                      setFormValues({
                        ...formValues,
                        question_id: question.data.id,
                        answer: parseInt(e.target.value),
                      });
                      setOtherOptionSelected(false);
                      setFormErrors({ error: "", error_class: "" });
                      nextButtonActive.current = true;
                    }

                    const item = document.getElementById("buttons");
                    if (item) {
                      item.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });
                    }
                  }}
                  optionImage={optionData.image ? optionData.image.image_path : null }
                />

                {formValues.answer === optionData.id && 
                 ((optionData.option_value === "Other" && test.language === "en") || 
                  (optionData.option_value === "آخر" && test.language === "ar")) && (
                  <div className="other-input-field">
                    <Input
                      label={test.language === "en" ? "Specify other" : "تحديد آخر"}
                      maxLength="100"
                      language={test.language}
                      value={formValues.other_option_value}
                      id="othervalue"
                      rtl={test.language === "en" ? false : true}
                      readOnly={false}
                      onChange={(e) => {
                        if (e.target.value !== " ") {
                          setFormValues({
                            ...formValues,
                            other_option_value: e.target.value,
                          });
                          nextButtonActive.current = e.target.value.trim() !== "";
                          setFormErrors({ error: "", error_class: "" });
                        }
                      }}
                      onClear={() => {
                        setFormValues({
                          ...formValues,
                          other_option_value: "",
                        });
                        nextButtonActive.current = false;
                      }}
                      error={formErrors.error}
                      required={true}
                      optionImage={optionData.image ? process.env.REACT_APP_IMG_URL+"/"+ optionData.image.image_path : null }
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderMultipleChoice = () => {
    const options = getOptions(question);

    return (
      <div className={getMethodologyClass("single-choice-take-test medium-box-wrapper multiple-choice-take-test")}>
        {renderQuestionHeader()}
        
        <div className={`select-option-text-guide mt-24 ${isNoneOfTheAboveSelected ? "disable-sub-mc-intruction-heading" : ""}`}>
          <Text type="body-text-1" fontWeight="medium-font">
            {(() => {
              let sub_heading = test.language === "en" 
                ? "Select 1 or more of the following"
                : "حدد خيار واحد أو أكثر من الخيارات أدناه";

              const questionData = question.questionData;
              if (questionData) {
                if (questionData.exact) {
                  sub_heading = test.language === "en"
                    ? `Select ${questionData.exact} of the following`
                    : `اختر ${questionData.exact} مما يلي`;
                } else if (questionData.min && questionData.max) {
                  sub_heading = test.language === "en"
                    ? `Select between ${questionData.min} and ${questionData.max} of the following`
                    : `اختر بين ${questionData.max} و ${questionData.min} مما يلي`;
                } else if (questionData.min) {
                  sub_heading = test.language === "en"
                    ? `Select ${questionData.min} or more of the following`
                    : `اختر ${questionData.min} أو أكثر مما يلي`;
                } else if (questionData.max) {
                  sub_heading = test.language === "en"
                    ? `Select up to ${questionData.max} of the following`
                    : `اختر ما يصل إلى ${questionData.max} مما يلي`;
                }
              }
              return sub_heading;
            })()}
          </Text>
        </div>

        <div className="test-form-hold mt-32">
          <div className={`${test.language === "en" ? "singlechoice-option-wrap" : "singlechoice-option-wrap singlechoice-option-arabic-wrap"}`}>
            {options?.map((optionData) => (
              <div key={optionData.id}>
                <TesterQuestionOptions
                  id={`custom-checks-${optionData.id}`}
                  type="multiplechoice"
                  value={String(optionData.id)}
                  label={optionData.option_value}
                  isChecked={formValues.option_id.includes(optionData.id)}
                  onChange={(e) => {
                    let none_of_the_above_selected = false;
                    let other_selected = false;

                    if (e.currentTarget.checked) {
                      if ((test.language === "en" && optionData.option_value === "None of the above") || 
                          (optionData.option_value === "لا شيء مما بالأعلى" && test.language === "ar")) {
                        formValues.option_id = [];
                        formValues.option_id.push(parseInt(e.target.value));
                        setFormValues({
                          ...formValues,
                          question_id: question.data.id,
                          option_id: formValues.option_id,
                          is_none_above: parseInt(e.target.value),
                          other_option_value: "",
                        });
                        setOtherOptionSelected(false);
                        setIsNoneOfTheAboveSelected(true);
                        none_of_the_above_selected = true;
                      } else {
                        setIsNoneOfTheAboveSelected(false);
                        if (formValues.option_id.includes(formValues.is_none_above)) {
                          formValues.option_id = formValues.option_id.filter(item => item !== formValues.is_none_above);
                        }
                        formValues.option_id.push(parseInt(e.target.value));
                        if ((test.language === "en" && optionData.option_value === "Other") || 
                            (optionData.option_value === "آخر" && test.language === "ar")) {
                          setFormValues({
                            ...formValues,
                            question_id: question.data.id,
                            option_id: formValues.option_id,
                            other_option_value: "",
                          });
                          setOtherOptionSelected(true);
                        } else {
                          setFormValues({
                            ...formValues,
                            question_id: question.data.id,
                            option_id: formValues.option_id,
                          });
                          setOtherOptionSelected(false);
                        }
                      }
                    } else {
                      if ((test.language === "en" && optionData.option_value === "None of the above") || 
                          (optionData.option_value === "لا شيء مما بالأعلى" && test.language === "ar")) {
                        setIsNoneOfTheAboveSelected(false);
                      }
                      if ((test.language === "en" && optionData.option_value === "Other") || 
                          (optionData.option_value === "آخر" && test.language === "ar")) {
                        setOtherOptionSelected(false);
                        setFormErrors({ error: "", error_class: "" });
                      }
                      formValues.option_id = formValues.option_id.filter(id => id !== optionData.id);
                      setFormValues({
                        ...formValues,
                        question_id: question.data.id,
                        option_id: formValues.option_id,
                      });
                    }

                    updateNextBtnStateThruOptions(question, formValues, none_of_the_above_selected);
                  }}
                  optionImage={optionData.image ? optionData.image.image_path : null }
                />

                {formValues.option_id.includes(optionData.id) && 
                 ((optionData.option_value === "Other" && test.language === "en") || 
                  (optionData.option_value === "آخر" && test.language === "ar")) && (
                  <div className="other-input-field">
                    <Input
                      label={test.language === "en" ? "Specify other" : "تحديد آخر"}
                      maxLength="100"
                      language={test.language}
                      value={formValues.other_option_value}
                      id="othervalue"
                      rtl={test.language === "en" ? false : true}
                      readOnly={false}
                      onChange={(e) => {
                        if (e.target.value !== " ") {
                          setFormValues({
                            ...formValues,
                            other_option_value: e.target.value,
                          });
                          setFormErrors({ error: "", error_class: "" });
                          updateNextBtnStateThruOther(question, formValues);
                        }
                      }}
                      onClear={() => {
                        setFormValues({
                          ...formValues,
                          other_option_value: "",
                        });
                      }}
                      error={formErrors.error}
                      required={true}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderRatingScale = () => (
    <div className={getMethodologyClass("single-choice-take-test medium-box-wrapper rating-scale-take-test")}>
      {renderQuestionHeader()}
      
      <div className="test-form-hold mt-32 rating-options">
        <div className={`${test.language === "en" ? "ratingscale-option-wrap" : "ratingscale-option-wrap ratingscale-option-arabic-wrap"}`}>
          <RatingScaleTester
            type={question.questionData.scale_type === "Emotions" ? "emoticons" : 
                  (question.questionData.scale_type === "Numeric" ? "numbers" : "stars")}
            scale={question.questionData.scale_length === "3 points" ? 3 : 
                   (question.questionData.scale_length === "5 points" ? 5 : 
                    (question.questionData.scale_length === "7 points" ? 7 : 10))}
            value={formValues.answer}
            labels={question.questionData.labels}
            onChange={(value) => {
              setFormValues({
                ...formValues,
                question_id: question.data.id,
                answer: value
              });
              nextButtonActive.current = true;
            }}
          />
        </div>
      </div>
    </div>
  );

  const renderLikertScale = () => {
    const options = getOptions(question);
    const subquestions = getSubquestions(question);
    
    if (!options || !Array.isArray(options)) {
      console.error('No options found for Likert scale question');
      return null;
    }

    return (
      <div className={getMethodologyClass("likert-scale-taking-test-view")}>
        {renderQuestionHeader()}
        
        <div className="survey-rating-options-wrap likret-scale-top-row none-for-mobile">
          <div className="bd-question-radio-data w-100">
            <div className="ans-list-repeat saved-ans-repeat w-100 mt-32">
              {question.data.question_type !== "likertscale_single" && (
                <div className="likert-option-left">
                  <div className="ans-icon">&nbsp;</div>
                  <div className="ans-data">&nbsp;</div>
                </div>
              )}
              <div className={`likert-option-right ${question.data.question_type === "likertscale_single" ? "likert-option-right-single" : ""}`}>
                {options.map((option, index) => (
                  <label htmlFor={`radio-${index}`} key={index}>
                    <p>{option.option_value}</p>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={`likert-innerview ${likertActive ? "likert-active-view" : ""}`}>
          {(question.data.question_type === "likertscale" || 
            question.data.question_type === "likertscale_single") && 
           subquestions?.map((subQuestion, subquestionindex) => (
            <div className="ans-list-wrap likert-scale-option-add" 
                 key={subquestionindex} 
                 id={`subquestion-${subquestionindex}`}>
              <div className="ans-list-repeat saved-ans-repeat">
                {question.data.question_type !== "likertscale_single" && (
                  <div className="likert-option-left">
                    <div className="ans-data">{subQuestion.subquestion}</div>
                  </div>
                )}
                <div className={`likert-option-right ${question.data.question_type === "likertscale_single" ? "likert-option-right-single" : ""}`}>
                  <div className="survey-rating-options-wrap w-100">
                    <div className="bd-question-radio-data w-100">
                      <div className="checkboxes radio_btn_class d-flex rating-answer-options-radio w-100">
                        {options.map((option, index) => (
                          <div className="likert-view-option-repeat" key={subquestionindex + '-' + index}>
                            <span onClick={() => movetonextlikertsubquestion(subquestionindex)}>
                              <RadioButton
                                id={`option-select-${subquestionindex + '-' + index}`}
                                isSelectedlabelBold={true}
                                name={`option-select-${subquestionindex}`}
                                value={option.id}
                                checked={formValues.option_id.some(subquestion => 
                                  subquestion.subquestionid === subQuestion.id && 
                                  subquestion.optionid === option.id
                                )}
                                onChange={(e) => {
                                  isActiveQuestionId.current = question.data.id;
                                  likertAttemptedSubQuestion.current++;

                                  // Remove existing answer for this subquestion if any
                                  const updatedOptions = formValues.option_id.filter(subquestion => 
                                    subquestion.subquestionid !== subQuestion.id
                                  );
                                  
                                  // Add new answer
                                  updatedOptions.push({ 
                                    subquestionid: subQuestion.id, 
                                    optionid: option.id 
                                  });

                                  // Sort by subquestion ID
                                  const sortedOptions = updatedOptions.sort((a, b) => {
                                    if (a.subquestionid < b.subquestionid) return -1;
                                    return 0;
                                  });

                                  setFormValues({
                                    ...formValues,
                                    question_id: question.data.id,
                                    option_id: sortedOptions,
                                    totalSubQuestions: subquestions.length,
                                  });

                                  // Update next button state
                                  // Check if all subquestions have been answered
                                  const answeredSubquestions = new Set(sortedOptions.map(opt => opt.subquestionid));
                                  if (answeredSubquestions.size >= subquestions.length) {
                                    nextButtonActive.current = true;
                                  } else {
                                    nextButtonActive.current = false;
                                  }
                                }}
                                label={option.option_value}
                              />
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderRankingScale = () => {
    const options = getOptions(question);
    if (!options || !Array.isArray(options)) {
      console.error('No options found for ranking scale question');
      return null;
    }

    // Only set ranking options if they haven't been set yet
    if (rankingOptions.length === 0) {
      let ranking_options = [...options];
      if (question.data.randomize_ranking_options === 1) {
        ranking_options = shuffle(ranking_options);
      }
      setRankingOptions(ranking_options);
      initialRankingOrder.current = ranking_options.map(option => option.id);
    } else if (initialRankingOrder.current.length === 0) {
      // Ensure baseline is captured if options were set elsewhere
      initialRankingOrder.current = rankingOptions.map(option => option.id);
    }

    return (
      <div className={getMethodologyClass("ranking-scale-taking-test-view")}>
        {renderQuestionHeader()}
        
        <div className="select-option-text-guide mt-24">
          <Text type="body-text-1" fontWeight="medium-font">
            {test.language === "en" ? "Drag the items below to reorder them" : "ترتيب العناصر حسب الأهمية"}
          </Text>
        </div>

        <div className="test-form-hold">
          <div className="ranking-data-wrap">
            <div className="rank-dragable-list-wrap" style={{ position: 'relative' }}>
              <DragDropContext
                onDragEnd={(param) => {
                  setPlaceholderProps({});
                  if (!param.destination) return;

                  const srcI = param.source.index;
                  const desI = param.destination.index;

                  const newRankingOptions = [...rankingOptions];
                  newRankingOptions.splice(
                    desI,
                    0,
                    newRankingOptions.splice(srcI, 1)[0]
                  );

                  const updatedOrderIds = newRankingOptions.map(option => option.id);
                  const hasBeenReordered = JSON.stringify(initialRankingOrder.current) !== JSON.stringify(updatedOrderIds);

                  // Mark that the user has interacted with the ranking list. Once the user
                  // interacts (drags) we will enable the Next button even if they revert
                  // back to the original order.
                  rankingInteracted.current = true;
                  nextButtonActive.current = true;

                  setRankingOptions(newRankingOptions);
                  setFormValues(prev => ({
                    ...prev,
                    question_id: question.data.id,
                    option_id: updatedOrderIds,
                  }));
                }}
                onDragStart={handleDragStart}
                onDragUpdate={handleDragUpdate}
              >
                <Droppable droppableId="droppable-1">
                  {(provided, snapshot) => (
                    <div className="w-100" ref={provided.innerRef} {...provided.droppableProps}>
                      {rankingOptions.map((optionData, index) => (
                        <Draggable
                          key={index}
                          draggableId={`draggable-${index}`}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              className={`ranking-list-repeat saved-ans-repeat ${snapshot.isDragging ? 'draggable-element-start' : ''}`}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <div className="ranking-count">
                                <p>{('0' + (index + 1)).slice(-2)}</p>
                              </div>

                              <div
                                className={`option-holder ${snapshot.isDragging ? 'draggable-inner-element-start' : ''}`}
                                {...provided.dragHandleProps}
                              >
                                <Icon value="move object" />
                                <Text display={"inline"} type={"body-text-2"}>
                                  {optionData.option_value}
                                </Text>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {!isEmpty(placeholderProps) && (
                        <div
                          className="placeholder"
                          style={{
                            top: placeholderProps.clientY,
                            left: placeholderProps.clientX,
                            height: placeholderProps.clientHeight,
                            width: placeholderProps.clientWidth,
                            position: "absolute",
                            borderColor: "#000000",
                            background: placeholderProps.background,
                            borderRadius: '10px',
                            border: placeholderProps.border,
                            margin: '0px 0px 10px'
                          }}
                        />
                      )}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFreeText = () => (
    <div className={getMethodologyClass("free-take-test-wrap medium-box-wrapper")}>
      {renderQuestionHeader()}
      
      <div className="test-form-hold mt-40" onClick={() => isTyping.current = true}>
        <TextArea
          autosize={true}
          label={test.language === "en" ? "Type your answer here" : "جاوب هنا"}
          language={test.language}
          value={formValues.answer == null ? '' : formValues.answer}
          error={formErrors.error}
          wrapClass="tester-text-area"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSubmitQuestion(e);
            }
          }}
          maxLength="300"
          onChange={(e) => {
            if (e.target.value !== " ") {
              setFormValues({ 
                ...formValues, 
                question_id: question.data.id, 
                answer: e.target.value 
              });
              nextButtonActive.current = e.target.value !== "" && e.target.value !== null;
            }
          }}
          onClear={() => {
            setFormValues({ ...formValues, "answer": '' });
          }}
        />
      </div>
    </div>
  );

  // Handle skip question
  const handleSkipQuestion = (e) => {
    e.preventDefault(); // Prevent form submission
    
    if (skippedCount < 2) {
      isSkipped.current = 1;
      setFormValues({
        ...formValues,
        question_id: isActiveQuestionId.current,
      });
      
      const skipData = {
        ...formValues,
        question_time_spent: questionTimeSpend,
        is_skipped: 1
      };

      // Call onSkipCountChange if provided
      if (onSkipCountChange) {
        onSkipCountChange(skippedCount + 1);
      }

      onNext(skipData);
    } else {
      showToast("You can skip maximum of 2 questions.", "error");
    }
  };

  return (
    <form onSubmit={handleSubmitQuestion}>
      <div className={getMethodologyClass("taking-test-page-height")}>
        {renderQuestionContent()}
      </div>
      
      {isPrototypeTest ? (
        renderPrototypeButtons()
      ) : (
        <div className={`taking-test-button-wrap d-flex justify-content-center${isTyping.current ? ' keyboard-active-button' : ''}${isOptional.current ? ' optional-button' : ''}`} id="buttons">
          
            <Button 
              type="primary" 
              size="large" 
              label={test.language === "en" ? "Next" : "التالي"} 
              iconRight={<Icon colorClass="gray-50-svg" value={test.language === "ar" ? "back-arrow" : "forward-arrow"} />} 
              state={nextButtonActive.current ? 'active' : 'disabled'} 
            />
         
          
          {isOptional.current ? (
          
              <Button 
                type="ghost" 
                size="medium" 
                label={test.language === "en" ? "Skip" : "تخطي"} 
                iconRight={<Icon colorClass="gray-900-svg" value={test.language === "ar" ? "backward" : "forward"} />} 
                onClick={handleSkipQuestion}
                cssclass="ml-16"
              />
           
          ) : null}
        </div>
      )}
    </form>
  );
};

export default GlobalQuestion; 